import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import Page from '@afosto/app-components-react/Page';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import DataGrid from '@afosto/app-components-react/DataGrid';
import { coupons as couponsApi } from '@afosto/order/api/Coupons';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import CouponCategoryForm from '../../components/CouponCategoryForm';
import Grid from '@material-ui/core/Grid';
import translations from './translations';
import { couponFormatter } from './formatters';
import CouponCategoryPreview from '../../components/CouponCategoryPreview';
import SECONDS_IN_A_DAY from '../../utils/constants';

const fetchCouponCategories = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => couponsApi.getCouponCategories(requestOptions),
    {
      transformData: data =>
        data.reduce(
          (acc, row) => [
            ...acc,
            {
              name: row.name,
              id: row.id,
              createdAt: row.created_at,
              updatedAt: row.updated_at,
              expiresAfter: row.defaults?.expires_after / SECONDS_IN_A_DAY,
              isUnlimited: row.defaults?.usage?.is_unlimited,
              limit: row.defaults?.usage?.limit,
            },
          ],
          [],
        ),
    },
  );

const CouponCategories = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:coupons:read']);
  const intl = useIntl();
  const [showCouponCategoryForm, setShowCouponCategoryForm] = useState(false);
  const [category, setCategory] = useState(null);
  const [isFetchingCategory, setIsFetchingCategory] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const dataSource = useDataGrid(fetchCouponCategories, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.categoryName',
            defaultMessage: 'Category name',
            description: 'The column name for column name',
          }),
          type: 'coupon',
        },
        {
          key: 'expiresAfter',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.expiresAfter',
            defaultMessage: 'Expires after',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'isUnlimited',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.isUnlimited',
            defaultMessage: 'Is unlimited',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'limit',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.limit',
            defaultMessage: 'Limit',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'createdAt',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column name',
          }),
          type: 'datetime',
        },
        {
          key: 'updatedAt',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCategories.updatedAt',
            defaultMessage: 'Updated at',
            description: 'The column name for column name',
          }),
          type: 'datetime',
        },
      ],
    },
  });

  const { activeItem: activeItemModule } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  const createCouponCategory = async formData => {
    try {
      const formattedData = {
        name: formData?.name,
        defaults: {
          expires_after: Number(formData?.expiresAfter * SECONDS_IN_A_DAY),
          usage: {
            is_unlimited: formData?.isUnlimited,
            limit: Number(formData?.limit),
          },
        },
      };
      await couponsApi.createCouponCategory(formattedData);
      setShowCouponCategoryForm(false);
      dataSource.reloadDataSource();
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponCategoryCreated), {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponCategoryNotCreated), {
        variant: 'error',
      });
    }
  };

  const updateCouponCategory = async formData => {
    try {
      const formattedData = {
        name: formData?.name,
        defaults: {
          expires_after: Number(formData?.expiresAfter * SECONDS_IN_A_DAY),
          usage: {
            is_unlimited: formData?.isUnlimited,
            limit: Number(formData?.limit),
          },
        },
      };
      const response = await couponsApi.updateCouponCategory(activeItem, formattedData);
      const { data } = response;
      setCategory(data);
      dataSource.reloadDataSource();
      setShowCouponCategoryForm(false);
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponCategoryUpdated), {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponCategoryNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCategory = async id => {
      setIsFetchingCategory(true);
      try {
        const categoryResponse = await couponsApi.getCouponCategory(id);
        const { data } = categoryResponse;
        if (isMounted) {
          setCategory(data);
        }
      } catch (e) {
        setCategory(null);
      } finally {
        setIsFetchingCategory(false);
      }
    };

    if (activeItem) {
      fetchCategory(activeItem);
    } else {
      setCategory(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem, enqueueSnackbar, intl]);

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid wrap="nowrap" container>
      <Page
        title={intl.formatMessage(translations.title)}
        fullWidth
        primaryAction={{
          text: intl.formatMessage(translations.create.button),
          action: () => setShowCouponCategoryForm(true),
        }}
        style={{ flex: 1 }}
      >
        <DataGrid dataSource={dataSource} formatters={{ coupon: couponFormatter }} />
      </Page>
      <FormDrawer
        isOpen={showCouponCategoryForm}
        onClose={() => setShowCouponCategoryForm(false)}
        drawerWidth="420px"
        title={intl.formatMessage(translations.createCouponCategoryDrawer.title)}
      >
        <CouponCategoryForm
          onSubmit={category ? updateCouponCategory : createCouponCategory}
          onCancel={() => setShowCouponCategoryForm(false)}
          category={category}
        />
      </FormDrawer>
      <CouponCategoryPreview
        isOpen={activeItem}
        isLoading={isFetchingCategory}
        onClose={() => setActiveItem(null)}
        onEdit={() => setShowCouponCategoryForm(true)}
        category={category}
        title={category?.name}
      />
    </Grid>
  );
};

export default CouponCategories;
